/* eslint-disable camelcase */
import { graphql, useStaticQuery } from "gatsby";
import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { useCoursesContext } from "../../../context/CoursesContext";
import { PageContext } from "../../../context/PageContext";
import useTranslations from "../../../hooks/useTranslations";
import { CourseFormat } from "../../../models/courses/CourseFormat";
import Button, { ButtonSize } from "../../Buttons/Button";
import LoadingIndicator from "../../LoadingIndicator/LoadingIndicator";
import PortableText, { PortableTextBody } from "../../PortableText";
import { PortableTextColorTheme } from "../../PortableText/PortableText";
import CoursePossibilitiesFilters, {
  CourseItemData
} from "../CoursePossibilitiesFilters/CoursePossibilitiesFilters";
import CourseInfoBlockItem from "./CourseInfoBlockItem";

interface CourseInfoBlockProps {
  listHeading?: string;
  portableTextContent?: PortableTextBody | undefined;
  portableTextSecondaryContent?: PortableTextBody | undefined;
}

interface NoAvailableCoursesQueryResult {
  sanitySiteSettings: {
    _rawNoAvailableCoursesContent?: PortableTextBody;
  };
}

const defaultNumberOfCourses = 10;

const CourseInfoBlock: FC<CourseInfoBlockProps> = ({
  listHeading,
  portableTextContent,
  portableTextSecondaryContent
}) => {
  const { coursePageData } = useContext(PageContext);
  const { course } = coursePageData ?? {};

  const {
    state: { learnsterCourse, loading }
  } = useCoursesContext();

  const {
    sanitySiteSettings: { _rawNoAvailableCoursesContent }
  } = useStaticQuery<NoAvailableCoursesQueryResult>(graphql`
    query NoAvailableCoursesQuery {
      sanitySiteSettings {
        _rawNoAvailableCoursesContent(resolveReferences: { maxDepth: 7 })
      }
    }
  `);

  const [paginateBy, setPaginateBy] = useState(defaultNumberOfCourses);
  const [paginatedCourses, setPaginatedCourses] = useState<CourseItemData[]>([]);

  const translations = useTranslations();

  const [selectedLocation, setSelectedLocation] = useState<string | null>("all");
  const [isBlendedFilterEnabled, setIsBlendedFilterEnabled] = useState<boolean>(false);
  const [filteredCourses, setFilteredCourses] = useState<CourseItemData[]>([]);

  const overrideFormatsMap = useMemo<Record<string, CourseFormat>>(
    () =>
      Object.fromEntries(
        [
          { courseId: course?.courseId, format: course?.format },
          ...(course?.linkedCourses ?? [])
        ].map(({ courseId, format }) => [courseId, format])
      ),
    [course?.courseId, course?.format, course?.linkedCourses]
  );

  const childCourses = useMemo<CourseItemData[]>(
    () =>
      learnsterCourse?.childCourses
        ?.sort(
          (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
        )
        ?.map(item => {
          const numberOfPlaces =
            (item.max_participants ?? 0) - (item?.statistics?.students_total ?? 0);

          return {
            id: item.id,
            name: item.name,
            course: item.course,
            start_date: item.start_date,
            end_date: item.end_date,
            format: overrideFormatsMap?.[item.course.id] || item.format,
            location: item?.location,
            numberOfPlaces: numberOfPlaces <= 0 ? 0 : numberOfPlaces,
            price:
              Number(item?.price) <= 0
                ? translations?.priceFree!
                : `${item?.price} ${item?.currency}`
          };
        }) ?? [],
    [learnsterCourse?.childCourses, overrideFormatsMap, translations?.priceFree]
  );

  const locationOptions = [
    {
      title: translations?.courseFormatAllOption!,
      value: "all"
    },
    ...(
      learnsterCourse?.childCourses?.reduce(
        (acc: Array<{ title: string; value: string }>, item) => {
          const existingItem = acc?.find(i => i.value === item?.location?.city);
          if (!existingItem) {
            if (item?.location?.city) {
              acc.push({
                title: item?.location?.city ?? "",
                value: item?.location?.city ?? ""
              });
            }
          }
          return acc;
        },
        []
      ) ?? []
    ).sort((a, b) => a.title.localeCompare(b.title))
  ];

  const showBlendedFilter = useMemo(
    () =>
      childCourses?.length > 1 &&
      childCourses?.some(
        item => item?.format?.toLocaleLowerCase()?.trim() !== CourseFormat.Blended
      ),
    [childCourses]
  );

  const showLocationFilter = locationOptions?.length > 1;

  const showMoreCourses = () => setPaginateBy(paginateBy + defaultNumberOfCourses);

  const handleResetFilters = () => {
    setIsBlendedFilterEnabled(false);
    setSelectedLocation("all");
  };

  useEffect(() => {
    if (childCourses?.length > 0) {
      const coursesData = childCourses?.filter(item => {
        let result = true;
        if (isBlendedFilterEnabled) {
          result = item?.format === CourseFormat.Blended;
        }

        result =
          result &&
          (selectedLocation === "all" || item?.location?.city === selectedLocation);

        return result;
      });

      setFilteredCourses(coursesData);
    }
  }, [childCourses, isBlendedFilterEnabled, selectedLocation]);

  useEffect(() => {
    setPaginatedCourses(filteredCourses?.slice(0, paginateBy));
  }, [filteredCourses, paginateBy]);

  return (
    <div className="relative w-full mx-auto px-4 md:px-6 max-w-[62.375rem] my-[3.75rem] lg:my-[6.25rem] first-of-type:mt-0">
      <div className="space-y-10">
        {portableTextContent && (
          <PortableText
            _rawBody={portableTextContent}
            colorTheme={PortableTextColorTheme.Neutral}
          />
        )}
        <CoursePossibilitiesFilters
          className="p-5 md:p-10 rounded-lg border bg-brand-primary-100"
          title={listHeading}
          itemsCount={childCourses?.length}
          locationOptions={locationOptions}
          selectedLocation={selectedLocation}
          onLocationChange={setSelectedLocation}
          isBlendedFilterEnabled={isBlendedFilterEnabled}
          onBlendedFilterChange={setIsBlendedFilterEnabled}
          showBlendedFilter={showBlendedFilter}
          showLocationFilter={showLocationFilter}
          onFilterReset={handleResetFilters}
        >
          <>
            {loading ? (
              <LoadingIndicator />
            ) : filteredCourses?.length > 0 ? (
              paginatedCourses?.map((item, index) => (
                <CourseInfoBlockItem
                  key={`${item?.id}-${index}`}
                  index={index}
                  item={item}
                />
              ))
            ) : (
              _rawNoAvailableCoursesContent && (
                <PortableText
                  _rawBody={_rawNoAvailableCoursesContent}
                  colorTheme={PortableTextColorTheme.Neutral}
                  wide
                  addSections={false}
                />
              )
            )}
            {filteredCourses?.length > paginateBy && (
              <div className="flex justify-center w-full">
                <Button
                  color="primary"
                  size={ButtonSize.Normal}
                  type="button"
                  onClick={showMoreCourses}
                >
                  {translations.loadMoreButton} +
                </Button>
              </div>
            )}
          </>
        </CoursePossibilitiesFilters>
        {portableTextSecondaryContent && (
          <PortableText
            _rawBody={portableTextSecondaryContent}
            colorTheme={PortableTextColorTheme.Neutral}
          />
        )}
      </div>
    </div>
  );
};

export default CourseInfoBlock;

